import { useState, useRef, useCallback } from 'react';
import * as api from '../api/client';

/**
 * Intercepts navigation that would leave a session with unsaved messages,
 * showing a Save / Discard / Cancel dialog before proceeding.
 */
export function useNavGuard(activeId, messages) {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const pendingNavRef = useRef(null);
  const snapshotRef   = useRef([]);

  const getUnsavedMessages = useCallback(() =>
    messages.filter(
      m =>
        m.source !== 'history' &&
        !m.persistent &&
        (m.role === 'user' || m.role === 'assistant') &&
        typeof m.content === 'string' &&
        m.content.trim() !== ''
    ),
  [messages]);

  const saveUnsavedMessages = useCallback(async (sid) => {
    const toSave = snapshotRef.current;
    if (!sid || toSave.length === 0) return;
    for (const m of toSave) {
      if (!m.role || !m.content || typeof m.content !== 'string' || !m.content.trim()) continue;
      try {
        await api.pinMessage(sid, m.role, m.content);
      } catch (err) {
        console.error('SaveOnLeave: pinMessage failed', m, err);
      }
    }
  }, []);

  const guardedNavigate = useCallback((navigate) => {
    const unsaved = getUnsavedMessages();
    if (activeId && unsaved.length > 0) {
      snapshotRef.current = unsaved;
      pendingNavRef.current = navigate;
      setSaveDialogOpen(true);
    } else {
      navigate();
    }
  }, [activeId, getUnsavedMessages]);

  const handleDialogSave = useCallback(async () => {
    setSaveDialogOpen(false);
    const sidToSave = activeId;
    const navFn = pendingNavRef.current;
    pendingNavRef.current = null;
    await saveUnsavedMessages(sidToSave);
    navFn?.();
  }, [activeId, saveUnsavedMessages]);

  const handleDialogDiscard = useCallback(() => {
    setSaveDialogOpen(false);
    pendingNavRef.current?.();
    pendingNavRef.current = null;
  }, []);

  const handleDialogCancel = useCallback(() => {
    setSaveDialogOpen(false);
    pendingNavRef.current = null;
  }, []);

  const dismissDialog = useCallback(() => {
    setSaveDialogOpen(false);
    pendingNavRef.current = null;
  }, []);

  return {
    saveDialogOpen,
    snapshotRef,
    guardedNavigate,
    handleDialogSave,
    handleDialogDiscard,
    handleDialogCancel,
    dismissDialog,
  };
}
